import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiLogger } from '../utils/apiLogger';
import { toastService } from './toastService';
import { trialLockedMessage } from '../utils/trialLockedCopy';
import { safeInternalPath } from '../utils/safeInternalPath';
import { isViteDevProxyHost } from '../utils/viteDevHost';

// Debug configuration - only enable in development with explicit flag
const DEBUG_API_CLIENT = import.meta.env.DEV &&
  (typeof localStorage !== 'undefined' && localStorage.getItem('debugApiClient') === 'true');

// Configuration interfaces
export interface RequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  skipErrorHandling?: boolean;
  retries?: number;
  maxRetries?: number;
  cache?: boolean;
  cacheOnly?: boolean; // 🔧 NEW: Force cache-only mode - throw error if no cached data
  cacheTTL?: number;
  priority?: 'low' | 'normal' | 'high';
  performanceTracking?: boolean;
  suppressUpgradeToast?: boolean;
  /**
   * Backend error codes the caller expects and explains itself (WB-50: the
   * competitor-gap daily caps). A 429 with one of these `code`s gets no
   * generic toast and no Sentry report; every other error on the request
   * (including the same code on another status) keeps the normal handling.
   */
  expectedErrorCodes?: string[];
  /**
   * HTTP statuses the caller expects and handles itself, e.g. 404 for a
   * feature that is switched off (GEO with ENABLE_GEO off). No toast, no
   * console error, no Sentry report for those; every other status on the
   * request keeps the normal handling. Narrower than skipErrorHandling.
   */
  expectedStatuses?: number[];
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp?: string;
}

export interface ApiError {
  message: string;
  code: string;
  status: number;
  details?: any;
  requestId?: string;
  sentryEventId?: string;
  /** Uppercased HTTP method of the failed request (grouping/diagnostics). */
  method?: string;
  /** Normalised request path — query stripped, volatile ids replaced with :id. */
  endpoint?: string;
}

/**
 * Strip the query string and replace volatile path segments (UUIDs, numeric ids,
 * long hashes) with :id, so Sentry groups network errors per ENDPOINT — a raw
 * URL per event would explode one issue per keyword/project, while no URL at
 * all collapses every network failure in the app into a single anonymous
 * "Network Error" issue (the old behaviour).
 */
function normaliseEndpoint(url?: string): string | undefined {
  if (!url) return undefined;
  const path = url.split('?')[0];
  return path
    .split('/')
    .map((seg) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) ||
      /^\d+$/.test(seg) ||
      /^[0-9a-f]{16,}$/i.test(seg)
        ? ':id'
        : seg,
    )
    .join('/');
}

// ───────────────────────────────────────────────────────────────────────────
// WHAT A REJECTED REQUEST SAYS
// ───────────────────────────────────────────────────────────────────────────
//
// ── THE DEFECT ────────────────────────────────────────────────────────────
// This client configures no `validateStatus`, so axios rejects every non-2xx
// and the rejection is the raw `AxiosError`. `AxiosError.message` for a status
// failure is the string "Request failed with status code 422". Every caller
// that does the obvious thing — `catch (err) { setError(err.message) }` — puts
// a protocol number in front of a customer, and the onboarding wizard did
// exactly that on its very first step.
//
// The sentence the backend author wrote was already in the payload the whole
// time. `createErrorResponse` returns `{ code, message, details, timestamp }`,
// so `error.response.data.message` IS the reviewed, customer-safe copy — and
// `createApiError` below has always extracted it correctly, but only for toasts
// and Sentry. Nothing on the customer's path read it.
//
// ── WHY THE FIX IS HERE AND NOT IN EACH CALLER ────────────────────────────
// Because "surface the backend message" is one rule, and there are hundreds of
// call sites. Fixing it at the seam means a caller has to go out of its way to
// show the axios string rather than go out of its way to avoid it. The original
// error object is MUTATED rather than replaced, so `err.response`, `err.config`
// and `axios.isAxiosError(err)` keep working for every existing consumer: the
// only things that change are `message` (better) and `code`/`status` (new).

/**
 * Written copy for a rejection whose body carried no message of its own — an
 * HTML error page from a proxy, a gateway timeout, a dropped connection.
 *
 * Never a status code, and never "Request failed with status code N". Each line
 * says what happened and what the customer can do; none of them claims a cause
 * we did not observe.
 */
export function fallbackMessageForStatus(status: number): string {
  if (status === 0) {
    return 'We could not reach RnkRocket. Check your connection and try again.';
  }
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have access to that. Try signing in again, or contact support.';
  if (status === 404) return 'We could not find that. It may have been removed.';
  if (status === 408 || status === 504) {
    return 'That took too long to respond. Please try again in a moment.';
  }
  if (status === 429) return 'That is a lot of requests at once. Please wait a moment and try again.';
  if (status >= 500) {
      // 🔴 Was "Nothing was charged - please try again in a moment."
      //
      // A 5xx cannot establish that. The request may have completed work,
      // dispatched a provider call or metered spend and then failed on the way
      // back; a failed RESPONSE is not evidence that the WORK failed. Telling a
      // customer nothing was charged when we cannot know it is the same class of
      // dishonesty as congratulating them over a failed step.
      return 'Something went wrong on our side. Please try again in a moment.';
  }
  if (status >= 400) return 'We could not complete that request. Please check the details and try again.';
  return 'Something went wrong. Please try again.';
}

/**
 * Attach the backend's own message and code to a rejected request.
 *
 * Returns the SAME error instance. A non-axios throw (a cache miss, an abort,
 * a bug in a caller) is handed back untouched — rewriting a message we did not
 * receive from a server would be inventing one.
 */
export function enrichRejection(error: any): any {
  if (!error || typeof error !== 'object') return error;
  // Only rewrite what came back from an HTTP exchange. `isAxiosError` is the
  // axios-set flag; `config` narrows out anything else that happens to carry a
  // `response` property.
  if (!(error as any).isAxiosError && !(error as any).config) return error;

  const data = (error as any).response?.data;
  const status = (error as any).response?.status ?? 0;

  const backendMessage =
    data && typeof data === 'object' && typeof (data as any).message === 'string'
      ? (data as any).message.trim()
      : '';

  (error as any).status = status;
  const backendCode =
    data && typeof data === 'object' ? ((data as any).code ?? (data as any).error) : undefined;
  if (typeof backendCode === 'string' && backendCode) (error as any).code = backendCode;
  if (data && typeof data === 'object' && (data as any).details !== undefined) {
    (error as any).details = (data as any).details;
  }

  error.message = backendMessage || fallbackMessageForStatus(status);
  return error;
}

// Request/Response interceptor types
export type RequestInterceptor = (config: any) => any | Promise<any>;
export type ResponseInterceptor = (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;
export type ErrorInterceptor = (error: AxiosError) => Promise<AxiosError>;

/**
 * Standardised API Client Service
 * 
 * Features:
 * - Automatic authentication handling
 * - Request/response interceptors
 * - Retry logic with exponential backoff
 * - Error standardisation and reporting
 * - File upload/download capabilities
 * - Centralised configuration
 */
// Helper function to get environment variables
const getApiUrl = (): string => {
  // 🔍 DIAGNOSTIC: Log environment detection
  // Housekeeping C7 (2026-08-16): the dev special-case now covers the whole
  // loopback class on port 5173 (localhost / 127.0.0.1 / *.localhost — e.g.
  // app.rnkrocket.localhost for RnkCookie CMP QA), not just localhost:5173.
  // See utils/viteDevHost.ts. The production fail-closed throw is unchanged.
  const isDevProxyHost = isViteDevProxyHost();

  if (DEBUG_API_CLIENT) {
    console.log('🔍 API CLIENT - getApiUrl() diagnostic:', {
      isDevProxyHost,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'undefined',
      port: typeof window !== 'undefined' ? window.location.port : 'undefined',
      currentLogic: 'VITE_API_URL → loopback-class :5173 proxy → throw error',
      timestamp: new Date().toISOString()
    });
  }

  // 1. VITE_API_URL takes priority (production/Vercel deployments)
  if (import.meta.env.VITE_API_URL) {
    if (DEBUG_API_CLIENT) {
      console.log('🔍 API CLIENT - Using VITE_API_URL:', import.meta.env.VITE_API_URL);
    }
    return import.meta.env.VITE_API_URL;
  }

  // Use process.env for Jest compatibility
  if (typeof process !== 'undefined' && process.env?.VITE_API_URL) {
    return process.env.VITE_API_URL;
  }

  // For Jest tests, use the global mock
  if (typeof global !== 'undefined' && (global as any).import?.meta?.env?.VITE_API_URL) {
    return (global as any).import.meta.env.VITE_API_URL;
  }

  // 2. In development with the Vite proxy (loopback-class host on :5173),
  // use the relative /api path ("Proxy is King")
  if (isDevProxyHost) {
    if (DEBUG_API_CLIENT) {
      console.log('🔍 API CLIENT - Using /api baseURL for dev proxy host (Vite proxy)');
    }
    return '/api';
  }

  // 3. FAIL CLOSED: Don't silently fall back to localhost:3001 in production
  // This prevents deployed builds from accidentally hitting localhost
  const errorMessage = 'API URL not configured. Set VITE_API_URL environment variable for production deployments.';
  console.error('🚨 API CLIENT ERROR:', errorMessage, {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
    port: typeof window !== 'undefined' ? window.location.port : 'unknown',
    env: import.meta.env.MODE
  });
  throw new Error(errorMessage);
};

// Performance monitoring interfaces
interface PerformanceMetrics {
  requestId: string;
  url: string;
  method: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  status?: number;
  size?: number;
  cached?: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
  size: number;
}

export class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;
  private maxRetries: number = 3;
  private retryDelay: number = 1000;
  private cache: Map<string, CacheEntry> = new Map();
  private performanceMetrics: PerformanceMetrics[] = [];
  private maxCacheSize: number = 50 * 1024 * 1024; // 50MB
  private currentCacheSize: number = 0;
  private refreshPromise: Promise<string | null> | null = null;
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private performanceThresholds = {
    slow: 1000, // 1 second
    critical: 200 // 200ms for critical endpoints
  };

  constructor(baseURL: string = getApiUrl()) {
    this.baseURL = baseURL;
    this.client = axios.create({
      baseURL,
      timeout: 30000, // Default timeout for most requests
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor for authentication and performance tracking
    this.client.interceptors.request.use(
      (config: any) => {
        // Ensure headers object exists
        config.headers = config.headers || {};
        
        const token = localStorage.getItem('authToken');
        const defaultAuthHeader = this.client.defaults.headers.common['Authorization'];

        // 🔍 DEBUG: Log token state for every request (sensitive token data removed for security)
        if (DEBUG_API_CLIENT) {
          console.log('🔐 API CLIENT - Request interceptor token state:', {
            url: config.url,
            method: config.method?.toUpperCase(),
            hasLocalStorageToken: !!token,
            hasDefaultAuthHeader: !!defaultAuthHeader,
            skipAuth: !!config.skipAuth,
            timestamp: new Date().toISOString()
          });
        }

        // Check if we should skip auth
        if (config.skipAuth === true) {
          // Remove any Authorisation header (including default one from axios instance)
          delete config.headers.Authorization;
          if (DEBUG_API_CLIENT) {
            console.log('🔐 API CLIENT - SKIPPING AUTH due to skipAuth flag (removed Authorization header)');
          }
        } else if (token && !config.skipAuth) {
          config.headers.Authorization = `Bearer ${token}`;
          if (DEBUG_API_CLIENT) {
            console.log('🔐 API CLIENT - Set Authorization header from localStorage token');
          }
        } else if (!config.skipAuth) {
          if (DEBUG_API_CLIENT) {
            console.warn('🔐 API CLIENT - No token available for authenticated request!', {
              url: config.url,
              hasToken: !!token,
              skipAuth: config.skipAuth
            });
          }
        }

        // Add request ID for tracking
        const requestId = this.generateRequestId();
        config.headers['X-Request-ID'] = requestId;

        // Add timestamp
        config.headers['X-Request-Timestamp'] = new Date().toISOString();

        // Add priority header
        if (config.priority) {
          config.headers['X-Request-Priority'] = config.priority;
        }

        // Start performance tracking
        if (config.performanceTracking !== false) {
          this.startPerformanceTracking(requestId, config);
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling, retries, and performance tracking
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // End performance tracking
        const requestId = response.config?.headers?.['X-Request-ID'] as string;
        if (requestId) {
          this.endPerformanceTracking(requestId, response);
        }

        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as RequestConfig;

        // End performance tracking for failed requests
        const requestId = originalRequest?.headers?.['X-Request-ID'] as string;
        if (requestId) {
          this.endPerformanceTracking(requestId, error.response, true);
        }

        // Handle authentication errors with retry after token refresh
        // Skip retry if this request was already retried to prevent infinite loops
        if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
          const refreshed = await this.handleAuthError();
          if (refreshed) {
            // Retry the original request with new token
            const token = localStorage.getItem('authToken');
            if (token) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              // Mark as retry to prevent infinite loops
              (originalRequest as any)._retry = true;
              return this.client.request(originalRequest);
            }
          }
          return Promise.reject(error);
        }

        // Handle retry logic
        if (this.shouldRetry(error, originalRequest)) {
          const retries = (originalRequest.retries || 0) + 1;
          originalRequest.retries = retries;

          const delay = this.calculateRetryDelay(retries);
          await this.sleep(delay);

          return this.retryRequest(originalRequest);
        }

        // Handle and report error
        if (!originalRequest.skipErrorHandling) {
          this.handleError(error);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Handle authentication errors with improved session management
   * @returns true if token was successfully refreshed, false otherwise
   */
  private async handleAuthError(): Promise<boolean> {
    if (DEBUG_API_CLIENT) {
      console.log('🔐 API Client: Handling auth error, attempting token refresh...');
    }

    // Try to refresh token before redirecting.
    //
    // CONCURRENCY (2026-07-14 incident): a busy page with an expired access
    // token produces MANY simultaneous 401s. Every one of them must await the
    // SAME in-flight refresh (refreshAuthToken dedupes via this.refreshPromise)
    // and retry on its success. The old `!this.isRefreshing` guard made every
    // concurrent caller SKIP the refresh and fall through to logout — booting
    // the user (and wiping the fresh tokens) while the winning refresh was
    // mid-flight. Only a definitive refresh failure may reach the logout below.
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        const newToken = await this.refreshAuthToken();
        if (newToken) {
          if (DEBUG_API_CLIENT) {
            console.log('✅ API Client: Token refreshed successfully');
          }
          return true; // Token was refreshed, caller should retry request
        }
      } catch (error) {
        console.error('❌ API Client: Token refresh failed:', error);
        this.handleError(error as AxiosError);
      }
    }

    // Clear tokens and notify about session expiry
    this.clearAuthTokens();

    // Store current URL for return after login. currentPath is the app's own
    // location so it is inherently same-origin, but pass it through the shared
    // sanitiser for consistency/defence-in-depth — only a safe relative path is
    // ever written to localStorage.
    const currentPath = window.location.pathname + window.location.search;
    const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
    if (!publicPaths.some(p => currentPath.startsWith(p))) {
      const safeCurrentPath = safeInternalPath(currentPath, '');
      if (safeCurrentPath) {
        localStorage.setItem('returnUrl', safeCurrentPath);
      }
    }

    // Despatch session expired event for better UX
    window.dispatchEvent(new CustomEvent('auth:session-expired', {
      detail: {
        message: 'Your session has expired. Please log in again.',
        returnUrl: currentPath
      }
    }));

    // Redirect to login if not already there. Sanitise the stored returnUrl before
    // re-injecting it into the /login query — this is the value LoginForm reads back,
    // so a tainted one must be neutralised (and scrubbed from storage) here too.
    const isPublicPage = publicPaths.some(p => window.location.pathname.startsWith(p));
    if (!isPublicPage) {
      const storedReturnUrl = localStorage.getItem('returnUrl');
      const safeReturnUrl = safeInternalPath(storedReturnUrl, '');
      if (safeReturnUrl) {
        window.location.href = `/login?returnUrl=${encodeURIComponent(safeReturnUrl)}`;
      } else {
        if (storedReturnUrl) {
          localStorage.removeItem('returnUrl');
        }
        window.location.href = '/login';
      }
    }

    return false; // Token refresh failed or not attempted
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(attempt: number): number {
    return Math.min(this.retryDelay * Math.pow(2, attempt - 1), 10000);
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if request should be retried
   */
  private shouldRetry(error: AxiosError, config: RequestConfig): boolean {
    const retries = config.retries ?? 0;
    const maxRetries = config.maxRetries ?? this.maxRetries;

    // Don't retry if max retries reached
    if (retries >= maxRetries) {
      return false;
    }

    // Don't retry OAuth callback endpoints (authorisation codes are single-use)
    if (config.url?.includes('/oauth/callback') || config.url?.includes('/callback')) {
      return false;
    }

    // Don't retry authentication errors
    if (error.response?.status === 401) {
      return false;
    }

    // Retry 429 (rate limited) for safe read methods — backoff will resolve transient limits
    if (error.response?.status === 429) {
      const method = error.config?.method?.toUpperCase();
      return method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
    }

    // Check if this is a long-running request (AI/analysis endpoints)
    // Moved outside if blocks so it's in scope for both checks
    const isLongRunningRequest = error.config?.url?.includes('knowledge-graph') ||
                                 error.config?.url?.includes('content-recommendations') ||
                                 error.config?.url?.includes('content-intelligence') ||
                                 error.config?.url?.includes('content/generate') ||
                                 error.config?.url?.includes('competitors/');

    // Don't retry client errors (4xx) except for specific cases
    if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
      // Only retry timeout errors (408), but NOT for long-running AI requests
      return error.response.status === 408 && !isLongRunningRequest;
    }

    // Don't retry network errors (timeouts) for long-running requests
    if (!error.response && isLongRunningRequest) {
      return false;
    }

    // Retry network errors and server errors (5xx)
    return !error.response || (error.response.status >= 500);
  }

  /**
   * Retry failed request
   */
  private async retryRequest(config: RequestConfig): Promise<AxiosResponse> {
    const retries = config.retries ?? 0;

    if (DEBUG_API_CLIENT) {
      console.log(`[API Retry] Attempt ${retries} for ${config.method?.toUpperCase()} ${config.url}`);
    }

    return this.client.request(config);
  }

  /**
   * Transform response to standardised format
   */
  private transformResponse<T>(response: AxiosResponse): ApiResponse<T> {
    return response.data;
  }

  /**
   * Handle and report errors
   */
  private handleError(error: AxiosError): void {
    const apiError = this.createApiError(error);
    const responseData = error.response?.data as any;

    // An expected refusal the caller shows in its own words (see
    // RequestConfig.expectedErrorCodes). Not a technical error: no toast (a
    // daily cap is not "wait 1 minute"), no Sentry report.
    // Only on a 429, the only status these refusals come with (review LOW):
    // the same code on any other status is unexpected and keeps the normal
    // handling.
    const expectedCodes = (error.config as any)?.expectedErrorCodes;
    if (
      apiError.status === 429 &&
      Array.isArray(expectedCodes) &&
      typeof responseData?.code === 'string' &&
      expectedCodes.includes(responseData.code)
    ) {
      if (DEBUG_API_CLIENT) {
        console.info('[API] Expected refusal, shown by the caller:', { code: responseData.code, status: apiError.status });
      }
      return;
    }

    // A status the caller expects and handles itself (RequestConfig.expectedStatuses).
    const expectedStatuses = (error.config as any)?.expectedStatuses;
    if (Array.isArray(expectedStatuses) && expectedStatuses.includes(apiError.status)) {
      if (DEBUG_API_CLIENT) {
        console.info('[API] Expected status, handled by the caller:', { status: apiError.status });
      }
      return;
    }

    // Handle 402 INSUFFICIENT_TOKENS (AI token metering, M0.3) — user has
    // used their monthly AI token allowance. This is expected business logic,
    // not a technical error: show a friendly toast and do NOT report to Sentry.
    // Surfaces that own their own inline out-of-tokens UI can suppress this
    // toast via `suppressUpgradeToast: true` on the request config.
    if (apiError.status === 402 && responseData?.code === 'INSUFFICIENT_TOKENS') {
      const suppress = (error.config as any)?.suppressUpgradeToast === true;
      console.info('[API] AI tokens exhausted:', {
        operation: responseData.details?.operation,
        required: responseData.details?.required,
        available: responseData.details?.available,
      });
      if (!suppress) {
        toastService.outOfTokens(responseData.details);
      }
      return;
    }

    // Handle 402 Payment Required errors — differentiate limit-reached from no-access
    if (apiError.status === 402 && responseData?.upgradeRequired) {
      const featureName = responseData.feature?.replace(/_/g, ' ') || 'This feature';
      const suppress = (error.config as any)?.suppressUpgradeToast === true;

      // Trial-layer lock (domain-trial expired, wrong domain, owned-by-other, etc.)
      // gets a distinct toast + a broadcast event so the dashboard banner can refresh.
      const isTrialLock = typeof responseData.error === 'string' && responseData.error === 'TRIAL_LOCKED';
      if (isTrialLock) {
        const trialMessage = trialLockedMessage(responseData.message);
        console.info('[API] Trial locked:', {
          reason: responseData.trial?.reason,
          code: responseData.code,
        });
        if (!suppress) {
          toastService.trialLocked(trialMessage);
        }
        // Let UI listeners refresh trial state (e.g. TrialContext).
        window.dispatchEvent(new CustomEvent('trial:locked', { detail: responseData.trial }));
        return;
      }

      // Distinguish: LIMIT_EXCEEDED (user has access, hit monthly cap) vs UPGRADE_REQUIRED (no plan access)
      const isLimitExceeded = typeof responseData.error === 'string' && responseData.error.includes('LIMIT_EXCEEDED');

      if (isLimitExceeded) {
        console.info('[API] Plan limit reached:', { feature: responseData.feature, usage: responseData.currentUsage, limit: responseData.limit });
        if (!suppress) {
          toastService.planLimitReached(
            featureName,
            { current: responseData.currentUsage, limit: responseData.limit }
          );
        }
      } else {
        console.info('[API] Upgrade required:', { feature: responseData.feature, status: 402 });
        if (!suppress) {
          toastService.upgradeRequired(featureName);
        }
      }

      // Don't send to Sentry or show as error - this is expected business logic
      return;
    }

    // Show user-facing toast for rate limit errors (deduped via stable toast ID in toastService)
    if (apiError.status === 429) {
      const retryAfter = apiError.details?.retryAfter;
      const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 1;
      toastService.rateLimit(
        `Rate limited — please wait ${minutes} minute${minutes > 1 ? 's' : ''}.`
      );
    }

    // Check if this is a business logic error (limits, quotas) vs technical error
    const isBusinessLogicError = apiError.message.toLowerCase().includes('limit') ||
                                apiError.message.toLowerCase().includes('quota') ||
                                apiError.status === 403;

    if (isBusinessLogicError) {
      // For business logic errors, log as warning but don't send to Sentry
      if (DEBUG_API_CLIENT) {
        console.warn('[API Business Logic Error]', apiError);
      }
    } else {
      // For technical errors, log as error and send to Sentry
      console.error('[API Error]', apiError);

      // Report error to error tracking only for technical errors. Include the
      // method + normalised endpoint in the message so Sentry groups per
      // endpoint — a bare "Network Error" collapses every failing call in the
      // app into one undiagnosable issue (RNKROCKET-USERDASHBOARD-AZ).
      import('../utils/sentry').then(({ captureException }) => {
        const title = apiError.method && apiError.endpoint
          ? `${apiError.message} [${apiError.method} ${apiError.endpoint}]`
          : apiError.message;
        const errorWithContext = new Error(title);
        (errorWithContext as any).context = apiError;
        captureException(errorWithContext);
      }).catch(e => console.error("Failed to load Sentry for error reporting", e));
    }

    // Despatch error event for global error handling (both types)
    window.dispatchEvent(new CustomEvent('api:error', {
      detail: apiError
    }));
  }

  /**
   * Create standardised API error
   */
  private createApiError(error: AxiosError): ApiError {
    const response = error.response;
    const data = response?.data as any;

    return {
      message: data?.message || error.message || 'An unexpected error occurred',
      code: data?.code ?? data?.error ?? 'UNKNOWN_ERROR',  // FIXED: Support backend's {code, message, details} shape
      status: response?.status || 0,
      details: data?.details,
      requestId: error.config?.headers?.['X-Request-ID'] as string,
      sentryEventId: data?.metadata?.sentryEventId,
      method: error.config?.method ? error.config.method.toUpperCase() : undefined,
      endpoint: normaliseEndpoint(error.config?.url),
    };
  }

  /**
   * GET request with caching and performance tracking
   */
  async get<T = any>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('GET', url, undefined, config);
  }

  /**
   * POST request with performance tracking
   */
  async post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('POST', url, data, config);
  }

  /**
   * PUT request with performance tracking
   */
  async put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('PUT', url, data, config);
  }

  /**
   * PATCH request with performance tracking
   */
  async patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('PATCH', url, data, config);
  }

  /**
   * DELETE request with performance tracking
   * @param url - The endpoint URL
   * @param data - Optional request body (for bulk operations)
   * @param config - Optional request configuration
   */
  async delete<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('DELETE', url, data, config);
  }

  /**
   * Upload file
   */
  async upload<T = any>(url: string, file: File, config?: RequestConfig): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const uploadConfig: RequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await this.client.post<ApiResponse<T>>(url, formData, uploadConfig);
    return response.data;
  }

  /**
   * Download file
   */
  async download(url: string, filename?: string, config?: RequestConfig): Promise<void> {
    const response = await this.client.get(url, {
      ...config,
      responseType: 'blob',
    });

    // Create download link
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): number {
    return this.client.interceptors.request.use(interceptor);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): number {
    return this.client.interceptors.response.use(interceptor);
  }

  /**
   * Add error interceptor
   */
  addErrorInterceptor(interceptor: ErrorInterceptor): number {
    return this.client.interceptors.response.use(undefined, interceptor);
  }

  /**
   * Remove interceptor
   */
  removeInterceptor(type: 'request' | 'response', id: number): void {
    this.client.interceptors[type].eject(id);
  }

  /**
   * Update base URL
   */
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
    this.client.defaults.baseURL = baseURL;
  }

  /**
   * Update default headers
   */
  setDefaultHeaders(headers: Record<string, string>): void {
    Object.assign(this.client.defaults.headers, headers);
  }

  /**
   * Get current configuration
   */
  getConfig(): {
    baseURL: string;
    timeout: number;
    maxRetries: number;
    retryDelay: number;
  } {
    return {
      baseURL: this.baseURL,
      timeout: this.client.defaults.timeout as number,
      maxRetries: this.maxRetries,
      retryDelay: this.retryDelay,
    };
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<{
    timeout: number;
    maxRetries: number;
    retryDelay: number;
  }>): void {
    if (config.timeout !== undefined) {
      this.client.defaults.timeout = config.timeout;
    }
    if (config.maxRetries !== undefined) {
      this.maxRetries = config.maxRetries;
    }
    if (config.retryDelay !== undefined) {
      this.retryDelay = config.retryDelay;
    }
  }

  /**
   * Set authentication tokens and update headers immediately
   */
  setAuthTokens(accessToken: string, refreshToken?: string): void {
    // Store tokens in localStorage
    localStorage.setItem('authToken', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    // Immediately update the Authorisation header for all future requests
    this.client.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    if (DEBUG_API_CLIENT) {
      console.log('✅ API Client: Auth tokens set and headers updated immediately');
    }
  }

  /**
   * Clear authentication tokens and headers
   */
  clearAuthTokens(): void {
    // Remove tokens from localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');

    // Remove Authorisation header
    delete this.client.defaults.headers.common['Authorization'];

    if (DEBUG_API_CLIENT) {
      console.log('✅ API Client: Auth tokens and headers cleared');
    }
  }

  /**
   * Refresh authentication token
   */
  private async refreshAuthToken(): Promise<string | null> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      if (DEBUG_API_CLIENT) {
        console.log('🔐 API Client: No refresh token available');
      }
      return null;
    }

    try {
      if (DEBUG_API_CLIENT) {
        console.log('🔐 API Client: Attempting token refresh...');
      }

      // S3.5 — body field is `refreshToken` (camelCase) to match the new
      // backend contract. The backend rotates: returns a NEW access token
      // AND a NEW refresh token, with the old refresh token revoked. We
      // store the rotated refresh so the next 401 has a working handle.
      const response = await this.client.post('/auth/refresh', {
        refreshToken
      }, {
        skipAuth: true,
        skipErrorHandling: true
      } as RequestConfig);

      if (response.data?.success && response.data?.data?.token) {
        const newToken = response.data.data.token;
        const rotatedRefresh = response.data.data.refreshToken || newToken;

        this.setAuthTokens(newToken, rotatedRefresh);

        if (DEBUG_API_CLIENT) {
          console.log('✅ API Client: Token refreshed successfully (rotated)');
        }
        return newToken;
      }

      if (DEBUG_API_CLIENT) {
        console.warn('⚠️ API Client: Invalid refresh response');
      }
      return this.adoptSiblingRotation(refreshToken);
    } catch (error) {
      console.error('❌ API Client: Token refresh failed:', error);
      return this.adoptSiblingRotation(refreshToken);
    }
  }

  /**
   * Cross-tab heal (2026-07-14): tabs share localStorage but race their
   * refreshes — if ANOTHER tab rotated first, our refresh token is already
   * revoked and our attempt fails. Before treating that as a dead session,
   * check whether localStorage now holds a DIFFERENT refresh token than the
   * one we sent: if so, a sibling tab won the race and its rotated tokens are
   * already stored — adopt them as our success instead of logging the user out.
   */
  private adoptSiblingRotation(sentRefreshToken: string): string | null {
    const currentRefresh = localStorage.getItem('refreshToken');
    const currentAccess = localStorage.getItem('authToken');
    if (currentRefresh && currentAccess && currentRefresh !== sentRefreshToken) {
      if (DEBUG_API_CLIENT) {
        console.log('🔐 API Client: Adopting sibling tab\'s rotated tokens');
      }
      this.client.defaults.headers.common['Authorization'] = `Bearer ${currentAccess}`;
      return currentAccess;
    }
    return null;
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/health', { skipAuth: true, skipErrorHandling: true });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Performance monitoring methods
   */
  private startPerformanceTracking(requestId: string, config: any): void {
    const metric: PerformanceMetrics = {
      requestId,
      url: config.url,
      method: config.method?.toUpperCase() || 'GET',
      startTime: performance.now()
    };
    
    this.performanceMetrics.push(metric);
  }

  private endPerformanceTracking(requestId: string, response?: AxiosResponse, isError: boolean = false): void {
    const metricIndex = this.performanceMetrics.findIndex(m => m.requestId === requestId);
    if (metricIndex === -1) return;

    const metric = this.performanceMetrics[metricIndex];
    metric.endTime = performance.now();
    metric.duration = metric.endTime - metric.startTime;
    metric.status = response?.status || 0;
    
    if (response?.data) {
      metric.size = JSON.stringify(response.data).length;
    }

    // Log slow requests
    if (metric.duration > this.performanceThresholds.slow) {
      if (DEBUG_API_CLIENT) {
        console.warn(`[API Performance] Slow request detected: ${metric.method} ${metric.url} took ${metric.duration.toFixed(2)}ms`);
      }
    }

    // Log critical endpoint performance
    if (this.isCriticalEndpoint(metric.url) && metric.duration > this.performanceThresholds.critical) {
      if (DEBUG_API_CLIENT) {
        console.error(`[API Performance] Critical endpoint exceeded threshold: ${metric.method} ${metric.url} took ${metric.duration.toFixed(2)}ms (threshold: ${this.performanceThresholds.critical}ms)`);
      }
    }

    // Despatch performance event
    window.dispatchEvent(new CustomEvent('api:performance', {
      detail: { ...metric, isError }
    }));

    // Keep only last 100 metrics to prevent memory leaks
    if (this.performanceMetrics.length > 100) {
      this.performanceMetrics = this.performanceMetrics.slice(-100);
    }
  }

  private isCriticalEndpoint(url: string): boolean {
    const criticalEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/refresh',
      '/dashboard',
      '/projects',
      '/health'
    ];
    
    return criticalEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Caching methods
   */
  private generateCacheKey(url: string, config?: RequestConfig): string {
    const method = config?.method || 'GET';
    const params = config?.params ? JSON.stringify(config.params) : '';
    return `${method}:${url}:${params}`;
  }

  private getCachedResponse<T>(cacheKey: string): ApiResponse<T> | null {
    const entry = this.cache.get(cacheKey);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(cacheKey);
      this.currentCacheSize -= entry.size;
      return null;
    }

    return entry.data;
  }

  private setCachedResponse<T>(cacheKey: string, data: ApiResponse<T>, ttl: number): void {
    const serialized = JSON.stringify(data);
    const size = serialized.length;

    // Check cache size limits
    if (this.currentCacheSize + size > this.maxCacheSize) {
      this.evictOldestEntries(size);
    }

    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      ttl,
      size
    };

    this.cache.set(cacheKey, entry);
    this.currentCacheSize += size;
  }

  private evictOldestEntries(requiredSpace: number): void {
    const entries = Array.from(this.cache.entries())
      .sort(([, a], [, b]) => a.timestamp - b.timestamp);

    let freedSpace = 0;
    for (const [key, entry] of entries) {
      this.cache.delete(key);
      this.currentCacheSize -= entry.size;
      freedSpace += entry.size;

      if (freedSpace >= requiredSpace) break;
    }
  }

  /**
   * Enhanced request methods with caching, performance tracking, and comprehensive logging
   */
  private async makeRequest<T>(
    method: string,
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    // 🚀 ENHANCED LOGGING: Log API request firing
    const requestId = apiLogger.logFiring(method, url, { ...config, data });
    const startTime = performance.now();

    // Check if this is a lighthouse/PageSpeed request that needs extended timeout
    const isLighthouseRequest = url.includes('lighthouse') || url.includes('pagespeed');
    // Check if this is a knowledge graph request that needs extended timeout for large sites
    const isKnowledgeGraphRequest = url.includes('knowledge-graph') || url.includes('build-domain-graph');
    // Check if this is a competitor analysis request that needs extended timeout for sitemap-based crawling
    const isCompetitorAnalysisRequest = url.includes('competitors/analyze') || url.includes('competitors/discover');
    // Check if this is an AI content generation request
    const isContentGenerationRequest = url.includes('content/generate') || url.includes('content-recommendations') || url.includes('content-intelligence');
    const extendedTimeout = isLighthouseRequest ? 120000 :
                           isKnowledgeGraphRequest ? 180000 : // 3 minutes for AI-powered knowledge graph
                           isContentGenerationRequest ? 180000 : // 3 minutes for AI content generation
                           isCompetitorAnalysisRequest ? 180000 : // 3 minutes for competitor analysis (sitemap crawling + AI)
                           undefined;
    
    // Ensure skipAuth and other custom flags are properly preserved
    const requestConfig: any = {
      ...config,
      method,
      url,
      data,
      // Set timeout: explicit config takes precedence, then auto-detected extended timeout
      timeout: config?.timeout || extendedTimeout,
      // Explicitly preserve custom flags
      skipAuth: config?.skipAuth,
      skipErrorHandling: config?.skipErrorHandling,
      retries: config?.retries,
      maxRetries: config?.maxRetries,
      cache: config?.cache,
      cacheTTL: config?.cacheTTL,
      priority: config?.priority,
      performanceTracking: config?.performanceTracking
    };
    
    // Log timeout configuration for extended timeout requests
    if (isLighthouseRequest || isKnowledgeGraphRequest || isCompetitorAnalysisRequest) {
      if (DEBUG_API_CLIENT) {
        console.log('🚀 API Client: Using extended timeout for request', {
          url,
          method,
          timeout: extendedTimeout,
          requestType: isLighthouseRequest ? 'lighthouse' :
                       isKnowledgeGraphRequest ? 'knowledge-graph' :
                       'competitor-analysis',
          timestamp: new Date().toISOString()
        });
      }
    }
    const requestKey = this.generateCacheKey(url, requestConfig);

    // 🔧 CRITICAL FIX: Enhanced deduplication with lighthouse-specific logging
    if (this.pendingRequests.has(requestKey)) {
      if (DEBUG_API_CLIENT) {
        const isLighthouseDedup = url.includes('lighthouse') || url.includes('pagespeed');
        if (isLighthouseDedup) {
          console.log(`🚫 API Client: LIGHTHOUSE REQUEST DEDUPLICATION - Blocking duplicate lighthouse request for ${method} ${url} [${requestId}]`, {
            originalRequestKey: requestKey,
            duplicateRequestId: requestId,
            requestType: 'lighthouse_deduplication',
            timestamp: new Date().toISOString()
          });
        } else {
          console.log(`🔄 API Client: Using pending request for ${method} ${url} [${requestId}]`);
        }
      }
      return this.pendingRequests.get(requestKey) as Promise<ApiResponse<T>>;
    }

    const requestPromise = (async () => {
      try {
        // Check cache first for GET requests
        if (method === 'GET' && config?.cache !== false) {
          const cached = this.getCachedResponse<T>(requestKey);
          if (cached) {
            if (DEBUG_API_CLIENT) {
              console.log(`💾 API Client: Cache hit for ${method} ${url} [${requestId}]`);
            }
            apiLogger.logResult(requestId, method, url, { status: 200, data: cached }, startTime);
            window.dispatchEvent(new CustomEvent('api:cache-hit', { detail: { url, method, cacheKey: requestKey } }));
            return cached;
          }

          // 🔧 CRITICAL FIX: Handle cache-only mode
          if (config?.cacheOnly) {
            if (DEBUG_API_CLIENT) {
              console.log(`🚫 API Client: Cache-only mode - no cached data available for ${method} ${url} [${requestId}]`);
            }
            const error = new Error(`No cached data available for ${url} (cache-only mode)`);
            (error as any).code = 'CACHE_MISS';
            (error as any).cacheOnly = true;
            apiLogger.logError(requestId, method, url, error, startTime);
            throw error;
          }
        }

        // 🎯 ENHANCED LOGGING: Log API request fired (sent)
        apiLogger.logFired(requestId, method, url);

        const response = await this.client.request<ApiResponse<T>>(requestConfig);

        // 🔧 CRITICAL FIX: Handle blob responses correctly for PDF export
        // For blob responses (like PDF exports), return the full axios response (not wrapped in ApiResponse)
        if (config?.responseType === 'blob') {
          if (DEBUG_API_CLIENT) {
            console.log('📄 API Client: Handling blob response for PDF export', {
              url,
              method,
              responseType: config.responseType,
              blobSize: (response.data as any)?.size || 0,
              blobType: (response.data as any)?.type || 'unknown',
              timestamp: new Date().toISOString()
            });
          }

          // ✅ ENHANCED LOGGING: Log successful blob response
          apiLogger.logResult(requestId, method, url, response, startTime);

          // For blob responses, return the full axios response object (with .data, .headers, .status)
          // This allows the caller to access response.data (the blob), response.headers, response.status
          return response as any;
        }

        // Cache successful GET responses
        if (method === 'GET' && config?.cache !== false && response.status === 200) {
          const ttl = config?.cacheTTL || 5 * 60 * 1000;
          this.setCachedResponse(requestKey, response.data, ttl);
        }

        // Invalidate cache for mutation operations (PUT, PATCH, POST, DELETE)
        if (['PUT', 'PATCH', 'POST', 'DELETE'].includes(method) && response.status >= 200 && response.status < 300) {
          // Extract resource path for cache invalidation
          const resourcePath = url.split('?')[0]; // Remove query parameters
          const segments = resourcePath.split('/').filter(Boolean);
          
          // Special case for validation endpoints - invalidate connections cache
          if (url.includes('/validate') && url.includes('/connections/')) {
            if (DEBUG_API_CLIENT) {
              console.log(`🗑️ API Client: Invalidating connections cache after validation`);
            }
            this.invalidateCacheByPattern(`/keywords/connections`);
          }
          
          // Invalidate related cache entries
          if (segments.length > 0) {
            const baseResource = segments[0]; // e.g., 'campaigns', 'projects'
            // Note: Don't use leading slash - cache keys are generated without it (e.g., 'GET:todos:')
            this.invalidateCacheByPattern(baseResource);
            
            // For specific resource updates (e.g., /campaigns/123), also invalidate broader patterns
            if (segments.length > 1) {
              this.invalidateCacheByPattern(resourcePath);
            }
          }
        }

        // ✅ ENHANCED LOGGING: Log successful API response
        apiLogger.logResult(requestId, method, url, response, startTime);

        return response.data;
      } catch (error) {
        // ❌ ENHANCED LOGGING: Log API error
        apiLogger.logError(requestId, method, url, error, startTime);
        // Carry the backend's own sentence out with the rejection — see
        // `enrichRejection`. The error instance is unchanged apart from
        // `message`, `code`, `status` and `details`, so every existing consumer
        // that reads `err.response` still works.
        throw enrichRejection(error);
      } finally {
        this.pendingRequests.delete(requestKey);
      }
    })();

    this.pendingRequests.set(requestKey, requestPromise);
    return requestPromise;
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): PerformanceMetrics[] {
    return [...this.performanceMetrics];
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    size: number;
    maxSize: number;
    entries: number;
    hitRate?: number;
  } {
    return {
      size: this.currentCacheSize,
      maxSize: this.maxCacheSize,
      entries: this.cache.size
    };
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    this.currentCacheSize = 0;
  }

  /**
   * Invalidate cache entries by URL pattern
   * This is critical for ensuring updates are reflected immediately
   */
  invalidateCacheByPattern(pattern: string): void {
    const keysToDelete: string[] = [];
    
    for (const [cacheKey] of this.cache) {
      if (cacheKey.includes(pattern)) {
        keysToDelete.push(cacheKey);
      }
    }
    
    for (const key of keysToDelete) {
      const entry = this.cache.get(key);
      if (entry) {
        this.currentCacheSize -= entry.size;
        this.cache.delete(key);
      }
    }
    
    if (keysToDelete.length > 0) {
      if (DEBUG_API_CLIENT) {
        console.log(`🗑️ API Client: Invalidated ${keysToDelete.length} cache entries matching pattern: ${pattern}`);
      }
    }
  }

  /**
   * Clear performance metrics
   */
  clearPerformanceMetrics(): void {
    this.performanceMetrics = [];
  }
}

// Create and export default instance
export const apiClient = new ApiClient();

// Export types for use in other files